import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Paper,
  Switch,
  Typography,
} from '@mui/material';
import { Unstable_Grid2 as Grid } from '@mui/material';
import { Coins, Plus, Save } from 'lucide-react';
import {
  AdvanceTokenPurchaseLimitConfig,
  CreateTokenPackInput,
  TokenPackCurrency,
  TokenPackPriceInput,
  UpdateTokenPackInput,
  useAdvanceTokenPurchaseLimits,
  useCreateTokenPack,
  useToggleTokenPackStatus,
  useTokenPacks,
  useUpdateAdvanceTokenPurchaseLimits,
  useUpdateTokenPack,
} from '../../services/tokenPack.service';
import { TokenPackCard } from '../../components/admin/TokenPackCard';
import { TokenPackFormData, TokenPackFormDialog } from '../../components/admin/TokenPackFormDialog';
import { EmptyState } from '../../components/admin/EmptyState';
import { useNotification } from '../../hooks/useNotification';
import { FormattedNumberTextField } from '../../components/common/FormattedNumberTextField';

const trimToNull = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed || null;
};

export const TokenPackManagement: React.FC = () => {
  const notification = useNotification();
  const { data: tokenPacks, isLoading, error } = useTokenPacks();
  const {
    data: purchaseLimitData,
    isLoading: isPurchaseLimitLoading,
    error: purchaseLimitError,
  } = useAdvanceTokenPurchaseLimits();
  const createTokenPackMutation = useCreateTokenPack();
  const updateTokenPackMutation = useUpdateTokenPack();
  const toggleTokenPackStatusMutation = useToggleTokenPackStatus();
  const updatePurchaseLimitsMutation = useUpdateAdvanceTokenPurchaseLimits();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [selectedTokenPack, setSelectedTokenPack] = useState<TokenPackFormData | undefined>(
    undefined
  );
  const [purchaseLimitConfig, setPurchaseLimitConfig] = useState<AdvanceTokenPurchaseLimitConfig>({
    dailyTokenLimit: null,
    monthlyTokenLimit: null,
  });

  useEffect(() => {
    if (purchaseLimitData?.config) {
      setPurchaseLimitConfig(purchaseLimitData.config);
    }
  }, [purchaseLimitData]);

  const purchaseLimitChanged = useMemo(() => {
    if (!purchaseLimitData?.config) return false;

    return (
      purchaseLimitConfig.dailyTokenLimit !== purchaseLimitData.config.dailyTokenLimit ||
      purchaseLimitConfig.monthlyTokenLimit !== purchaseLimitData.config.monthlyTokenLimit
    );
  }, [purchaseLimitConfig, purchaseLimitData]);

  const monthlyLimitInvalid =
    purchaseLimitConfig.dailyTokenLimit !== null &&
    purchaseLimitConfig.monthlyTokenLimit !== null &&
    purchaseLimitConfig.monthlyTokenLimit < purchaseLimitConfig.dailyTokenLimit;

  const handlePurchaseLimitValueChange = (
    field: keyof AdvanceTokenPurchaseLimitConfig,
    value: number | undefined
  ) => {
    setPurchaseLimitConfig((previous) => ({
      ...previous,
      [field]: value !== undefined && Number.isFinite(value) && value > 0 ? value : 1,
    }));
  };

  const handlePurchaseLimitUnlimitedChange = (
    field: keyof AdvanceTokenPurchaseLimitConfig,
    unlimited: boolean
  ) => {
    setPurchaseLimitConfig((previous) => ({
      ...previous,
      [field]: unlimited ? null : previous[field] || 1000000,
    }));
  };

  const handleCreateTokenPack = () => {
    setSelectedTokenPack(undefined);
    setDialogMode('create');
    setIsDialogOpen(true);
  };

  const handleEdit = (publicId: string) => {
    const tokenPack = tokenPacks?.find((item) => item.publicId === publicId);
    if (!tokenPack) {
      notification.error('Credit pack not found');
      return;
    }

    setSelectedTokenPack({
      publicId: tokenPack.publicId,
      code: tokenPack.code,
      name: tokenPack.name,
      descriptionEn: tokenPack.descriptionEn ?? '',
      descriptionVi: tokenPack.descriptionVi ?? '',
      tokenAmount: tokenPack.tokenAmount,
      prices: (['VND', 'USD'] as TokenPackCurrency[]).map((currency) => {
        const price = tokenPack.prices.find((item) => item.currency === currency);
        return {
          currency,
          price: price?.price ?? 0,
          isActive: price?.isActive ?? false,
        };
      }),
      isActive: tokenPack.isActive,
      sortOrder: tokenPack.sortOrder,
    });
    setDialogMode('edit');
    setIsDialogOpen(true);
  };

  const handleToggleStatus = async (publicId: string, isActive: boolean) => {
    try {
      await toggleTokenPackStatusMutation.mutateAsync({ publicId, isActive });
      notification.success(`Credit pack ${isActive ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      notification.error('Failed to update credit pack status');
    }
  };

  const handleSavePurchaseLimits = async () => {
    if (monthlyLimitInvalid) {
      notification.error('Monthly credit limit must be greater than or equal to daily credit limit.');
      return;
    }

    try {
      await updatePurchaseLimitsMutation.mutateAsync(purchaseLimitConfig);
      notification.success('Advance credit purchase limits updated successfully');
    } catch (saveError: unknown) {
      const errorMessage =
        (saveError as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to update advance credit purchase limits';
      notification.error(errorMessage);
    }
  };

  const handleFormSubmit = async (data: TokenPackFormData) => {
    const prices = data.prices.map<TokenPackPriceInput>((price) => ({
      currency: price.currency,
      amountMinor:
        price.currency === 'USD' ? Math.round(price.price * 100) : Math.round(price.price),
      isActive: price.isActive,
    }));

    try {
      if (dialogMode === 'create') {
        const createData: CreateTokenPackInput = {
          code: data.code.trim().toUpperCase(),
          name: data.name,
          descriptionEn: trimToNull(data.descriptionEn),
          descriptionVi: trimToNull(data.descriptionVi),
          tokenAmount: data.tokenAmount,
          prices,
          isActive: data.isActive,
          sortOrder: data.sortOrder,
        };
        await createTokenPackMutation.mutateAsync(createData);
        notification.success('Credit pack created successfully');
      } else {
        const updateData: UpdateTokenPackInput = {
          publicId: data.publicId!,
          name: data.name,
          descriptionEn: trimToNull(data.descriptionEn),
          descriptionVi: trimToNull(data.descriptionVi),
          tokenAmount: data.tokenAmount,
          prices,
          isActive: data.isActive,
          sortOrder: data.sortOrder,
        };
        await updateTokenPackMutation.mutateAsync(updateData);
        notification.success('Credit pack updated successfully');
      }

      setIsDialogOpen(false);
      setSelectedTokenPack(undefined);
    } catch (error: unknown) {
      const errorMessage =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        `Failed to ${dialogMode === 'create' ? 'create' : 'update'} credit pack`;
      notification.error(errorMessage);
    }
  };

  return (
    <Box className="h-full bg-slate-50 p-8 dark:bg-slate-900">
      <Box className="mb-6 flex items-start justify-between">
        <Box>
          <Typography variant="h4" className="mb-2 font-bold text-gray-900 dark:text-slate-100">
            Advance Credit Management
          </Typography>
          <Typography variant="body2" className="text-gray-600 dark:text-slate-400">
            Manage purchasable advance credit packs shown in the user subscription page
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Plus size={20} />}
          onClick={handleCreateTokenPack}
          sx={{
            backgroundColor: '#f59e0b',
            '&:hover': {
              backgroundColor: '#d97706',
            },
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          Create Token Pack
        </Button>
      </Box>

      <Paper
        elevation={0}
        className="mb-6 rounded-xl bg-white p-5 dark:bg-slate-800"
        sx={{
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
        }}
      >
        <Box className="mb-4 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <Box>
            <Typography variant="h6" className="font-semibold text-gray-900 dark:text-slate-100">
              Purchase Limits
            </Typography>
            <Typography variant="body2" className="mt-1 text-gray-600 dark:text-slate-400">
              Configure how many advance tokens each user can purchase per UTC day and month.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={
              updatePurchaseLimitsMutation.isPending ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <Save size={18} />
              )
            }
            onClick={handleSavePurchaseLimits}
            disabled={
              !purchaseLimitChanged ||
              isPurchaseLimitLoading ||
              updatePurchaseLimitsMutation.isPending ||
              monthlyLimitInvalid
            }
            sx={{
              backgroundColor: '#f59e0b',
              '&:hover': {
                backgroundColor: '#d97706',
              },
              '&:disabled': {
                backgroundColor: '#94a3b8',
              },
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {updatePurchaseLimitsMutation.isPending ? 'Saving...' : 'Save Limits'}
          </Button>
        </Box>

        {purchaseLimitError ? (
          <Alert severity="error">Failed to load advance credit purchase limits.</Alert>
        ) : isPurchaseLimitLoading ? (
          <Box className="flex h-24 items-center justify-center">
            <CircularProgress size={28} className="text-amber-500" />
          </Box>
        ) : (
          <Box className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <Box className="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
              <Box className="mb-3 flex items-center justify-between gap-4">
                <Typography variant="subtitle1" className="font-semibold">
                  Daily limit
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={purchaseLimitConfig.dailyTokenLimit === null}
                      onChange={(event) =>
                        handlePurchaseLimitUnlimitedChange('dailyTokenLimit', event.target.checked)
                      }
                      color="primary"
                    />
                  }
                  label="Unlimited"
                />
              </Box>
              <FormattedNumberTextField
                label="Max credits per day"
                value={purchaseLimitConfig.dailyTokenLimit ?? 0}
                onValueChange={(value) => handlePurchaseLimitValueChange('dailyTokenLimit', value)}
                disabled={purchaseLimitConfig.dailyTokenLimit === null}
                fullWidth
                inputProps={{ min: 1, step: 1000 }}
              />
            </Box>

            <Box className="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
              <Box className="mb-3 flex items-center justify-between gap-4">
                <Typography variant="subtitle1" className="font-semibold">
                  Monthly limit
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={purchaseLimitConfig.monthlyTokenLimit === null}
                      onChange={(event) =>
                        handlePurchaseLimitUnlimitedChange(
                          'monthlyTokenLimit',
                          event.target.checked
                        )
                      }
                      color="primary"
                    />
                  }
                  label="Unlimited"
                />
              </Box>
              <FormattedNumberTextField
                label="Max credits per month"
                value={purchaseLimitConfig.monthlyTokenLimit ?? 0}
                onValueChange={(value) =>
                  handlePurchaseLimitValueChange('monthlyTokenLimit', value)
                }
                disabled={purchaseLimitConfig.monthlyTokenLimit === null}
                error={monthlyLimitInvalid}
                helperText={
                  monthlyLimitInvalid
                    ? 'Monthly limit must be greater than or equal to daily limit.'
                    : undefined
                }
                fullWidth
                inputProps={{ min: 1, step: 1000 }}
              />
            </Box>
          </Box>
        )}
      </Paper>

      {isLoading ? (
        <Box className="flex h-[400px] items-center justify-center">
          <CircularProgress size={40} className="text-amber-500" />
        </Box>
      ) : error ? (
        <EmptyState
          Icon={Coins}
          title="Failed to load credit packs"
          description="Please try again later"
        />
      ) : !tokenPacks || tokenPacks.length === 0 ? (
        <EmptyState
          Icon={Coins}
          title="No credit packs available"
          description="Get started by creating your first advance credit pack"
          actionLabel="Create your first credit pack"
          onAction={handleCreateTokenPack}
        />
      ) : (
        <Grid container spacing={3}>
          {tokenPacks.map((tokenPack) => (
            <Grid key={tokenPack.publicId} xs={12} sm={6} md={4} lg={3}>
              <TokenPackCard
                tokenPack={tokenPack}
                onToggleStatus={handleToggleStatus}
                onEdit={handleEdit}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <TokenPackFormDialog
        open={isDialogOpen}
        mode={dialogMode}
        initialData={selectedTokenPack}
        isLoading={
          dialogMode === 'create'
            ? createTokenPackMutation.isPending
            : updateTokenPackMutation.isPending
        }
        onClose={() => {
          setIsDialogOpen(false);
          setSelectedTokenPack(undefined);
        }}
        onSubmit={handleFormSubmit}
      />
    </Box>
  );
};
